// src/components/PhotoAnnotatorToolbar.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * Toolbar for the photo annotator.
 *
 * Props:
 *  - tool: "pencil" | "brush" | "emoji"
 *  - selectedEmoji: string
 *  - emojis?: string[]   // optional list of emojis for the picker
 *  - onToolChange: (tool: string) => void
 *  - onEmojiChange: (emoji: string) => void
 *  - onToggleOptions: () => void
 *  - onClear: () => void
 */
export function PhotoAnnotatorToolbar({
  tool,
  selectedEmoji,
  emojis = ["😀", "😎", "😍", "🤔", "🔥", "👍", "🌴", "✈️", "📍"],
  onToolChange,
  onEmojiChange,
  onToggleOptions,
  onClear,
}) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiWrapperRef = useRef(null);

  const handleToolClick = (nextTool) => {
    onToolChange?.(nextTool);
    // Close emoji picker when switching away
    if (nextTool !== "emoji") {
      setShowEmojiPicker(false);
    }
  };

  const handleEmojiButtonClick = () => {
    // Always switch to emoji tool when clicking button
    onToolChange?.("emoji");
    setShowEmojiPicker((open) => !open);
  };

  const handleEmojiSelect = (emoji) => {
    onEmojiChange?.(emoji);
    setShowEmojiPicker(false);
  };

  // Close emoji picker if clicking outside of it
  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleClickOutside = (event) => {
      if (!emojiWrapperRef.current) return;
      if (!emojiWrapperRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showEmojiPicker]);

  const currentEmoji = selectedEmoji || emojis[0] || "😀";

  return (
    <div className="photo-annotator-toolbar">
      <div className="photo-annotator-toolbar-left">
        <button
          type="button"
          className={
            "photo-annotator-tool-button" +
            (tool === "pencil" ? " photo-annotator-tool-button--active" : "")
          }
          onClick={() => handleToolClick("pencil")}
        >
          ✏️
          <span className="photo-annotator-tool-label">Pencil</span>
        </button>

        <button
          type="button"
          className={
            "photo-annotator-tool-button" +
            (tool === "brush" ? " photo-annotator-tool-button--active" : "")
          }
          onClick={() => handleToolClick("brush")}
        >
          🖌️
          <span className="photo-annotator-tool-label">Brush</span>
        </button>

        {/* Emoji tool + inline dropdown */}
        <div
          ref={emojiWrapperRef}
          className="photo-annotator-emoji-tool-wrapper"
        >
          <button
            type="button"
            className={
              "photo-annotator-tool-button photo-annotator-tool-button--emoji" +
              (tool === "emoji" ? " photo-annotator-tool-button--active" : "")
            }
            onClick={handleEmojiButtonClick}
          >
            <span className="photo-annotator-tool-emoji-icon">
              {currentEmoji}
            </span>
            <span className="photo-annotator-tool-label">Emoji</span>
          </button>

          {showEmojiPicker && (
            <div className="photo-annotator-emoji-picker">
              <div className="photo-annotator-emoji-grid">
                {emojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="photo-annotator-emoji-option"
                    onClick={() => handleEmojiSelect(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="photo-annotator-toolbar-right">
        <button
          type="button"
          className="photo-annotator-tool-button"
          onClick={onToggleOptions}
        >
          ⚙️
          <span className="photo-annotator-tool-label">Options</span>
        </button>

        <button
          type="button"
          className="photo-annotator-tool-button"
          onClick={onClear}
        >
          🧹
          <span className="photo-annotator-tool-label">Clear</span>
        </button>
      </div>
    </div>
  );
}
